import React, { useCallback } from 'react';
import {
  View,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Text as RNText,
} from 'react-native';
import { Text } from '@/components';
import { useTranslation } from 'react-i18next';
import { Bell, Check, CheckCheck, Trash2, Bookmark, Star, Footprints, Trophy, MessageCircle } from 'lucide-react-native';
import theme from '@/constants/colors';
import { useNotificationsQuery, useMarkAsRead, useMarkAllAsRead, useDeleteNotification } from '@/lib/queries/notifications';
import { useAuth } from '@/context/AuthContext';
import { notificationToRoute } from '@/lib/deepLinks';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import styles from './notifications.styles';

function timeAgo(dateStr: string, t: (key: string, options?: any) => string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return t('notifications.justNow');
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return t('notifications.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('notifications.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return t('notifications.daysAgo', { count: days });
  return date.toLocaleDateString();
}

function getNotificationIcon(type: string): { icon: React.ReactNode; color: string } {
  switch (type) {
    case 'trail_save':
      return { icon: <Bookmark size={14} color={theme.accentSecondary} />, color: theme.accentSecondary };
    case 'trail_follow':
      return { icon: <Footprints size={14} color={theme.accentPrimary} />, color: theme.accentPrimary };
    case 'trail_comment':
      return { icon: <Star size={14} color={theme.accentSecondary} />, color: theme.accentSecondary };
    case 'achievement':
      return { icon: <Trophy size={14} color="#FFD700" />, color: '#FFD700' };
    case 'new_follower':
      return { icon: <MessageCircle size={14} color={theme.accentPrimary} />, color: theme.accentPrimary };
    case 'system':
    default:
      return { icon: <Bell size={14} color={theme.textSecondary} />, color: theme.textSecondary };
  }
}

export default function NotificationsScreen() {
  const { session } = useAuth();
  const token = session?.accessToken;
  const notificationsQuery = useNotificationsQuery(token);
  const markAsReadMutation = useMarkAsRead(token);
  const markAllAsReadMutation = useMarkAllAsRead(token);
  const deleteNotificationMutation = useDeleteNotification(token);
  const notifications = notificationsQuery.data?.items ?? [];
  const unreadCount = notificationsQuery.data?.unreadCount ?? 0;
  const refreshNotifications = notificationsQuery.refetch;
  const router = useRouter();
  const markAsRead = async (id: string) => { await markAsReadMutation.mutateAsync(id); };
  const markAllAsRead = async () => { await markAllAsReadMutation.mutateAsync(); };
  const deleteNotification = async (id: string) => { await deleteNotificationMutation.mutateAsync(id); };
  const { t } = useTranslation();
  const [refreshing, setRefreshing] = React.useState(false);
  const insets = useSafeAreaInsets();

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await refreshNotifications();
    setRefreshing(false);
  }, [refreshNotifications]);

  const handlePressNotification = useCallback(async (item: any) => {
    if (!item.read) {
      await markAsRead(item.id);
    }
    // same payload contract as push taps — one mapper for both
    const route = notificationToRoute(item.data);
    if (route) {
      router.push(route as any);
    }
  }, [markAsRead, router]);

  const renderItem = useCallback(({ item }: { item: any }) => (
    <TouchableOpacity
      style={[styles.notificationItem, !item.read && styles.notificationItemUnread]}
      onPress={() => handlePressNotification(item)}
      activeOpacity={0.7}
    >
      {/* Unread indicator */}
      {!item.read && <View style={styles.unreadDot} />}

      <View style={styles.notificationContent}>
        <View style={styles.notificationHeader}>
          <View style={[styles.notificationTypeIcon, { backgroundColor: getNotificationIcon(item.type).color + '22' }]}>
            {getNotificationIcon(item.type).icon}
          </View>
          <Text style={styles.notificationTitle} numberOfLines={1}>{item.title}</Text>
          <Text style={styles.notificationTime}>{timeAgo(item.createdAt, t)}</Text>
        </View>
        {item.body && <Text style={styles.notificationBody} numberOfLines={2}>{item.body}</Text>}
      </View>

      {/* Actions */}
      <View style={styles.notificationActions}>
        {!item.read && (
          <TouchableOpacity
            onPress={() => markAsRead(item.id)}
            style={styles.actionButton}
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          >
            <Check size={18} color={theme.textMuted} />
          </TouchableOpacity>
        )}
        <TouchableOpacity
          onPress={() => deleteNotification(item.id)}
          style={styles.actionButton}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
        >
          <Trash2 size={16} color={theme.textMuted} />
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  ), [handlePressNotification, markAsRead, deleteNotification]);

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={[styles.header, { paddingTop: insets.top + 8 }]}>
        <View style={styles.headerContent}>
          <View style={styles.headerLeft}>
            <Bell size={24} color={theme.accentPrimary} />
            <Text style={styles.headerTitle}>{t('notifications.title')}</Text>
            {unreadCount > 0 && (
              <View style={styles.headerBadge}>
                <RNText style={styles.headerBadgeText}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </RNText>
              </View>
            )}
          </View>
          {unreadCount > 0 && (
            <TouchableOpacity onPress={markAllAsRead} style={styles.markAllButton}>
              <CheckCheck size={18} color={theme.accentPrimary} />
              <Text style={styles.markAllText}>{t('notifications.markAllRead')}</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Notification list */}
      {notifications.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Bell size={56} color={theme.textMuted} />
          <Text style={styles.emptyTitle}>{t('notifications.noNotificationsYet')}</Text>
          <Text style={styles.emptySubtitle}>
            {t('notifications.noNotificationsSubtitle')}
          </Text>
        </View>
      ) : (
        <FlatList
          data={notifications}
          keyExtractor={(item) => item.id}
          renderItem={renderItem}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={theme.accentPrimary}
            />
          }
        />
      )}
    </View>
  );
}